/**
 * Legacy → canonical prop renames, applied to saved `websiteJson` before the
 * runtime deserialises it.
 *
 * WHY THIS EXISTS. Every canvas component is implemented twice: the editor
 * twin the organiser configures (main-frontend
 * `components/WebsiteBuilder/children/*`) and the runtime twin the published
 * site renders (`src/runtime/components/*`). Props travel between them BY
 * NAME through the saved JSON, and nothing enforces that the two sides agree
 * — so a renamed prop is a setting that silently does nothing on the live
 * site, with no error anywhere.
 *
 * SessionGroup drifted on five props. `minSelections`/`maxSelections` meant
 * the "min 2 / max 3" registration rule was enforced by NOTHING (there is no
 * server-side check either), and the three colour settings never applied.
 *
 * The editor is being renamed to the canonical (runtime) names, but the
 * persisted data speaks the OLD dialect — `minSelections: 2` is what is in
 * the database for every page ever saved. A rename alone would therefore
 * only fix pages an organiser happens to re-save. This table repairs every
 * published site at read time, with no re-save and no data migration.
 *
 * Canonical names come from BookingGroup, which never drifted and already
 * owns `selectionBorderColor` & co on both sides.
 *
 * ADDING AN ENTRY: key by the resolver name (the `type.resolvedName` in the
 * saved JSON), then `legacyName: "canonicalName"`. The prop-parity guard
 * imports this table so an aliased pair is treated as consumed rather than
 * reported as fresh drift.
 */
export const LEGACY_PROP_ALIASES: Record<string, Record<string, string>> = {
  SessionGroup: {
    minSelections: "min",
    maxSelections: "max",
    cardBorderColor: "selectionBorderColor",
    cardActiveBorderColor: "selectionActiveBorderColor",
    cardActiveBackgroundColor: "selectionActiveBackgroundColor",
  },
};

type NodeLike = {
  type?: { resolvedName?: string } | string;
  props?: Record<string, unknown>;
};

/**
 * Rewrite legacy prop names in place, for every node of an aliased
 * component.
 *
 * Rules, both load-bearing:
 *  - the canonical key WINS when both are present. A page saved by the new
 *    editor carries `min`, and may still drag a stale `minSelections`
 *    zombie along; the newer write must not be overwritten by the older one.
 *  - the legacy key is deleted either way, so the component never sees two
 *    spellings of the same setting.
 *
 * Idempotent: running twice is the same as running once.
 */
/**
 * Kept byte-for-byte in step with the editor twin's helper
 * (main-frontend `builders/website/utils/renameLegacyProps.ts`), so the
 * "keep these two files in sync" contract is mechanically checkable rather
 * than a matter of trust.
 *
 * The string branch is dead in practice — the engine's serialiser always
 * emits `type: {resolvedName}` — but the deserialiser tolerates a string, so
 * both helpers handle it rather than diverging over an edge neither will hit.
 */
function resolvedName(type: NodeLike["type"]): string | undefined {
  if (typeof type === "string") return type;
  if (type && typeof type === "object") return type.resolvedName;
  return undefined;
}

export function applyLegacyPropAliases(
  websiteJson: Record<string, unknown>,
): void {
  for (const node of Object.values(websiteJson)) {
    if (!node || typeof node !== "object") continue;
    const n = node as NodeLike;
    const name = resolvedName(n.type);
    if (!name) continue;
    const aliases = LEGACY_PROP_ALIASES[name];
    if (!aliases) continue;
    const props = n.props;
    if (!props || typeof props !== "object") continue;
    for (const [legacy, canonical] of Object.entries(aliases)) {
      if (!(legacy in props)) continue;
      if (props[canonical] === undefined) {
        props[canonical] = props[legacy];
      }
      delete props[legacy];
    }
  }
}
